import { useSignUp, useSignIn } from '@clerk/nextjs';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Button, Stack, Text, Divider, Alert, PinInput, Center, FocusTrap, PasswordInput } from '@mantine/core';
import { sign } from 'crypto';
import { on } from 'events';
import useAuth from '../lib/useAuth';
import * as Sentry from "@sentry/nextjs";
import { ClerkAPIError, ClerkAPIResponseError } from '@clerk/types';
import { all } from 'axios';

function GoogleIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" width="18" height="18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

type Props = {
  redirectUrl?: string;
  ssoCallbackUrl?: string;
  signUpIfMissing?: boolean;
  prompt: string;
  allowUsername?: boolean,
};

type Stage = 'email' | 'otp';

export default function SignInForm({ redirectUrl = '/my-account' , signUpIfMissing = false, prompt, allowUsername = false }: Props) {
  const { signUp } = useSignUp();
  const { signIn } = useSignIn();

  let ssoCallbackUrl = '/n/log-in/sso-callback';
  const router = useRouter();
  const [stage, setStage] = useState<Stage>('email');
  const [error, setError] = useState(router.query.sso_error === 'no_account' ? 'No account found for that Google account. Please log in with your email instead.' : '');
  const [working, setWorking] = useState(false);

  const {refreshUserInformation} = useAuth();
  const [safeIdentifier, setSafeIdentifier] = useState<string | undefined>(undefined);

  const emailForm = useForm({
    initialValues: { email: '' },
    validate: {
      email: (v) => (allowUsername ? null : (/^\S+@\S+$/.test(v) ? null : 'Please enter a valid email address')),
    },
  });


    const passwordForm = useForm({
    initialValues: { password: '' }
  
  });

  const [otpValue, setOtpValue] = useState('');
  const [hasPassword, setHasPassword] = useState(false);

  const isEmail = emailForm.values.email.includes('@');
  const [usePassword, setUsePassword] = useState(false);

    async function handlePassword(values: { password: string }) {
    
      setWorking(true);

      try
      {
        const {error} = await signIn.create({identifier: emailForm.values.email, password: values.password });
      
        if (error)
        {
          const code = (error as unknown as  ClerkAPIResponseError).errors?.[0]?.code;
          console.log(code);

          if (code=='form_password_pwned')
          {        
              throw new Error('This password has been exposed in a data breach and is not safe to use. Please sign in with the 6-digit code instead.');
          }
          else
          {
            throw error;
          }
        }

        const {error: e4} = await signIn.finalize();
          
          if (e4)
          {
            console.log(e4);
            Sentry.captureException(e4);
            throw e4;
          }
        refreshUserInformation();
        router.push(redirectUrl);
      }
      catch(err: any)
      {
        
        setError(err.message ?? 'Something went wrong. Please try again.');
    } finally {
      setWorking(false);
    }
  }

  async function handleEmail(values: { email: string }) {
    setError('');
    setWorking(true);
    try {

      const { error: e1 } = await signIn.create({ identifier: values.email, signUpIfMissing:(signUpIfMissing ?? false) });
    
      if (e1)
      {
         const code = (e1 as unknown as  ClerkAPIResponseError).errors?.[0]?.code;
        console.log(code);

        if (code=='form_identifier_not_found')
        {
          throw new Error(`No account found with that ${isEmail ? 'email' : 'username'}.`);
        }
        if (code =='form_param_format_invalid')
        {
          throw new Error(`The ${isEmail ? 'email' : 'username'} you entered is not valid. Please check and try again.`);
        }
        throw e1;
      }

      const {error: e5} = await signIn.emailCode.sendCode();

      if (e5)
      {
        throw e5;
      }

      setHasPassword(signIn.supportedFirstFactors?.some(f => f.strategy === 'password'));

      setSafeIdentifier(signIn.supportedFirstFactors.find(f => f.strategy === 'email_code')?.safeIdentifier);

      setStage('otp');
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong. Please try again.');
    } finally {
      setWorking(false);
    }
  }

  async function handleOtp() {
    if (otpValue.length !== 6) {
      setError('Please enter the full 6-digit code.');
      return;
    }
    setError('');
    setWorking(true);
    try {
       const {error} =await signIn.emailCode.verifyCode({ code: otpValue });

       if (error)
       {
        const code = (error as unknown as  ClerkAPIResponseError).errors?.[0]?.code;
        console.log(code);

        if (code=='sign_up_if_missing_transfer')
        {
          const {error:e3} = await signUp.create({transfer: true});

          if (e3)
          {
            console.log(e3);
            Sentry.captureException(e3);
            throw e3;
          }

          const {error: e4} = await signUp.finalize();
          
          if (e4)
          {
            console.log(e4);
            Sentry.captureException(e4);
            throw e4;
          }

        }
        else
        {
          throw error;
        }
       }

       else
       {

       const {error: e2} = await signIn.finalize();

       if (e2)
       {
          console.log(e2);
          throw e2;
       }
      }

       refreshUserInformation();
      router.push(redirectUrl);
      }
    catch (err: any) {      
      console.log(err);
      setError(err.message ?? 'Invalid code. Please try again.');
    } finally {
      setWorking(false);
    }
  }

  async function handleSocial(strategy: 'oauth_google') {
    setError('');
    setWorking(true);

    //console.log("redirectUrl: " + redirectUrl); 
    //console.log(strategy, redirectUrl, ssoCallbackUrl );

    try {
   
      const {error} = await signIn.sso({
        strategy,
        redirectUrl: redirectUrl,
        redirectCallbackUrl: ssoCallbackUrl + '?signUpIfMissing=' + (signUpIfMissing?1:0)        

      });

      console.log("SIGNIN", signIn);

      if (error)
      {
        console.log("ERROR!");
        Sentry.captureException(error);
        console.log(error);
        const code = (error as unknown as  ClerkAPIResponseError).errors?.[0]?.code;
        console.log(code);

        throw error;
        
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message ?? 'Something went wrong. Please try again.');
      
    }
    finally
    {
      setWorking(false);
    }
  }


  if (stage === 'otp') {
    return (
      <Stack>
               <div id="clerk-captcha"></div> 

                       {error && <Alert color="red" radius="md"><Text ta='center'>{error}</Text></Alert>}

      {(!usePassword) &&
      <Stack gap='sm'>


      
   
        <Text size="sm">
          We have sent a 6-digit code to <strong>{isEmail ? emailForm.values.email : safeIdentifier}</strong>. Please check your inbox and spam folder and enter the code below:
        </Text>





        <Center>
          <FocusTrap active>
          <PinInput
            length={6}
            type="number"
            oneTimeCode
            value={otpValue}
            onChange={setOtpValue}
            onComplete={async() => await handleOtp()}
          />
          </FocusTrap>
        </Center>

        <Button onClick={handleOtp} loading={working} fullWidth>
          Verify
        </Button>

                {(hasPassword && !usePassword) &&<Button variant="subtle" size="xs" onClick={() => { setUsePassword(true); setError(''); }}>
          Sign in with password instead
        </Button>}

      </Stack>}

      {(usePassword) &&
       <form onSubmit={passwordForm.onSubmit(async() => await handlePassword(passwordForm.values))}>


      <Stack gap='sm'>
        <FocusTrap active>
        <PasswordInput
        required
  label="Password"
  placeholder="Your password"
   {...passwordForm.getInputProps('password')}
   style={{ width: '100%', minWidth: 300 }}
/>
</FocusTrap>
 <Button type="submit" loading={working}>
           Log in
          </Button>

          <Button variant="subtle" size="xs" onClick={() => { setUsePassword(false); setError(''); }}>
          Forgotten your password? Try the 6-digit code instead
        </Button>

</Stack>
       </form>
  }




        <Button variant="subtle" size="xs" onClick={() => { setStage('email'); setOtpValue(''); setError(''); setUsePassword(false);}}>
          Use a different email
        </Button>

  



     </Stack>
    );
    
  }

  return (
    <Stack mt={0}>
       <div id="clerk-captcha"></div> 
      {error && <Alert color="red" radius="md" ta='center'>{error}</Alert>}

      <form onSubmit={emailForm.onSubmit(async() => await handleEmail(emailForm.values))}>
        <Stack>
         
         <FocusTrap active>
          <TextInput
            required
            label={prompt}
            placeholder="you@example.com"
            type={allowUsername ? 'text' : 'email'}
            style={{ width: '100%', minWidth: 300 }}
            {...emailForm.getInputProps('email')}
          />
          </FocusTrap>
          <Button type="submit" loading={working}>
            Continue with email {allowUsername?'/ username':''}
          </Button>
        </Stack>
      </form>


     

{/*}

 <Divider label="or" labelPosition="center" />
      <Center>
        <Button
        loading={working}
          variant="default"
          size="xs"
          leftSection={<GoogleIcon />}
          onClick={() => handleSocial('oauth_google')}
          style={{ width: 'fit-content' }}
        >
          Continue with Google
        </Button>
      </Center>*/}
    </Stack>
  );
}
